import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search } from 'lucide-react';
import { CircularProgress, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { createTag } from 'src/queries/query';
import TagBox from './tag-box';

export default function TagsSelector({
  tags = [],
  values,
  setFieldValue,
  orderTags = [],
  isDisplay = false,
  disabled = false,
  onTagsChange,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const createInputRef = useRef(null);
  const queryClient = useQueryClient();

  const createTagMutation = useMutation({
    mutationFn: createTag,
    onSuccess: async (response, variables) => {
      const created = response?.data?.data || response?.data || response;
      const newTag = {
        uuid: created?.uuid,
        name: created?.name || variables?.name,
        icon: created?.icon || variables?.icon || '',
        isActive: created?.isActive ?? true,
      };

      if (!newTag.uuid) {
        toast.error('Tag created but missing id. Refresh and try again.');
        return;
      }

      queryClient.setQueriesData({ queryKey: ['tags'] }, (old) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          if (old.some((t) => t.uuid === newTag.uuid)) return old;
          return [...old, newTag];
        }
        const list = old?.data?.data;
        if (Array.isArray(list)) {
          if (list.some((t) => t.uuid === newTag.uuid)) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: [...list, newTag],
            },
          };
        }
        return old;
      });

      await queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag created successfully!');

      const current =
        values?.tagUuids?.filter((uuid) => uuid && uuid !== '') ||
        orderTags?.map((t) => t.uuid || t).filter(Boolean) ||
        [];

      if (onTagsChange) {
        onTagsChange([...current, newTag.uuid]);
      } else if (setFieldValue) {
        setFieldValue('tagUuids', [...current, newTag.uuid]);
      }

      setIsCreating(false);
      setNewTagName('');
      setIsDropdownOpen(false);
      setSearchQuery('');
    },
    onError: (error) => {
      toast.error(`Failed to create tag: ${error.message}`);
    },
  });

  useEffect(() => {
    if (!isDisplay && values?.tagUuids && values.tagUuids.includes('')) {
      const cleanedTagUuids = values.tagUuids.filter((uuid) => uuid !== '');
      setFieldValue?.('tagUuids', cleanedTagUuids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCreating && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [isCreating]);

  const selectedUuids =
    values?.tagUuids?.filter((uuid) => uuid && uuid !== '') ||
    orderTags?.map((t) => t.uuid || t).filter(Boolean) ||
    [];

  const filteredTags =
    tags?.filter(
      (tag) =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !selectedUuids.includes(tag.uuid) &&
        tag.isActive !== false
    ) || [];

  const applyTagUuids = (newTagUuids) => {
    if (onTagsChange) {
      onTagsChange(newTagUuids);
    } else if (setFieldValue) {
      setFieldValue('tagUuids', newTagUuids);
    }
  };

  const addTag = (tag) => {
    if (selectedUuids.includes(tag.uuid)) return;
    applyTagUuids([...selectedUuids, tag.uuid]);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setIsCreating(false);
    setNewTagName('');
  };

  const removeTag = (tagUuid) => {
    applyTagUuids(selectedUuids.filter((uuid) => uuid !== tagUuid));
  };

  const handleStartCreate = (e) => {
    e.stopPropagation();
    setIsCreating(true);
    setNewTagName(searchQuery.trim());
  };

  const handleCancelCreate = (e) => {
    e?.stopPropagation();
    setIsCreating(false);
    setNewTagName('');
  };

  const handleSaveCreate = (e) => {
    e?.stopPropagation();
    const name = newTagName.trim();
    if (!name || createTagMutation.isPending) return;

    const exists = tags?.some(
      (tag) => tag.name.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      toast.error('A tag with this name already exists');
      return;
    }

    createTagMutation.mutate({
      name,
      icon: '',
      isActive: true,
    });
  };

  React.useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
      setIsCreating(false);
      setNewTagName('');
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const tagsToDisplay = React.useMemo(() => {
    if (isDisplay) {
      return orderTags;
    }

    if (
      orderTags?.length > 0 &&
      (!values?.tagUuids ||
        values.tagUuids.length === 0 ||
        (values.tagUuids.length === 1 && values.tagUuids[0] === ''))
    ) {
      return orderTags;
    }

    return tags?.filter((tag) => values?.tagUuids?.includes(tag.uuid)) || [];
  }, [isDisplay, orderTags, tags, values?.tagUuids]);

  return (
    <div className="w-full border-b border-gray-200 bg-[#f8f9fd] px-4 py-1">
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-sm mr-2">Tags:</span>
        <TagBox
          tags={tagsToDisplay}
          isDisplay={isDisplay || disabled}
          onRemove={
            disabled
              ? undefined
              : (uuid) => {
                  removeTag(uuid);
                }
          }
        />
        {!isDisplay && !disabled && (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
                if (isDropdownOpen) {
                  setIsCreating(false);
                  setNewTagName('');
                }
              }}
              className="flex items-center text-blue-600 text-sm px-2 py-1 hover:bg-blue-50 rounded"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add tag
            </button>

            {isDropdownOpen && (
              <div
                className="absolute left-0 mt-1 w-72 bg-white rounded-md shadow-lg border border-gray-200 z-50 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tags"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                <div className="max-h-48 overflow-y-auto">
                  {filteredTags.length > 0 ? (
                    filteredTags.map((tag) => (
                      <button
                        key={tag.uuid}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          addTag(tag);
                        }}
                        className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-gray-100"
                      >
                        {tag.icon && <span className="mr-2">{tag.icon}</span>}
                        {tag.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No matching tags
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 bg-gray-50 sticky bottom-0">
                  {isCreating ? (
                    <div className="flex items-center gap-1 p-2">
                      <input
                        ref={createInputRef}
                        type="text"
                        placeholder="New tag name"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSaveCreate(e);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCancelCreate(e);
                          }
                        }}
                        disabled={createTagMutation.isPending}
                        className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleSaveCreate}
                        disabled={
                          !newTagName.trim() || createTagMutation.isPending
                        }
                        title="Save"
                      >
                        {createTagMutation.isPending ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CheckIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelCreate}
                        disabled={createTagMutation.isPending}
                        title="Close"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleStartCreate}
                      className="flex items-center justify-center w-full px-3 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Create tag
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
